import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaLinkedin, FaGithub } from 'react-icons/fa';

// Helper function to interpolate colors
const interpolateColor = (color1: string, color2: string, factor: number) => {
  const result = color1.match(/\w\w/g)?.map((c, i) => {
    const value1 = parseInt(c, 16);
    const value2 = parseInt(color2.match(/\w\w/g)?.[i] || '00', 16);
    const value = Math.round(value1 + (value2 - value1) * factor);
    return value.toString(16).padStart(2, '0');
  });
  return `#${result?.join('')}`;
};

const Section = styled.section<{ $scrollProgress?: number }>`
  padding: 2.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  h2 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    background: ${props => {
      const start = '#2193b0';
      const end = '#6dd5ed';
      const color1 = interpolateColor(start, end, (props.$scrollProgress || 0) * 0.5);
      const color2 = interpolateColor(start, end, (props.$scrollProgress || 0) * 0.8);
      return `linear-gradient(90deg, ${color1}, ${color2} 85%)`;
    }};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const HeroSection = styled(Section)<{ $scrollProgress?: number }>`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: ${props => {
    const color1 = interpolateColor('#f5f7fa', '#e3e6ed', props.$scrollProgress || 0);
    const color2 = interpolateColor('#c3cfe2', '#a1b3d1', props.$scrollProgress || 0);
    return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
  }};
  border-radius: 8px;
  margin: 1rem 0;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    border-radius: 0;
    margin: 0;
    padding: 2rem 1.5rem;
    box-shadow: none;
  }

  h1 {
    font-size: 3.5rem;
    margin-bottom: 1rem;
    background: ${props => {
      const start = '#2193b0';
      const end = '#6dd5ed';
      const color1 = interpolateColor(start, end, (props.$scrollProgress || 0) * 0.3);
      const color2 = interpolateColor(start, end, (props.$scrollProgress || 0) * 0.7);
      return `linear-gradient(90deg, ${color1}, ${color2})`;
    }};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: 2.5rem;
    }
  }

  p {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    line-height: 1.6;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    & > ${HeroSection} {
      margin: 0 -1.5rem;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SocialLink = styled.a`
  color: #2193b0;
  text-decoration: none;
  font-weight: 500;
  padding: 0.75rem 1.25rem;
  border-radius: 4px;
  background: rgba(33, 147, 176, 0.1);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    font-size: 1.2em;
  }

  &:hover {
    background: rgba(33, 147, 176, 0.2);
    transform: translateY(-2px);
  }
`;

const Button = styled.a`
  display: inline-block;
  padding: 1rem 2.5rem;
  background: linear-gradient(90deg, #2193b0, #6dd5ed);
  color: white;
  border-radius: 30px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
  }
`;

const App: React.FC = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(window.scrollY / totalHeight, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <main>
      <Container>
        <HeroSection $scrollProgress={scrollProgress}>
          <div>
            <h1>Hi, I'm Máni Gudvardarson</h1>
            <p>
              Solution Architect & Full-Stack Engineer from Iceland, based in The Netherlands<br />
              15+ years building scalable digital solutions. Available for freelance work.
            </p>
            <Button href="#contact">Contact Me</Button>
          </div>
        </HeroSection>

        <Section id="about" $scrollProgress={scrollProgress}>
          <h2>About</h2>
          <p>
            Originally from Iceland, I'm a software engineer and solution architect with 15+ years of experience in full-stack development,
            cloud infrastructure (Azure & AWS), and digital innovation. Worked for Vodafone IS, Bunq, and
            the City of Amsterdam Innovation Team. When I'm not coding, you'll find me exploring the outdoors through running, biking,
            and skiing, or planning my next travel adventure. Currently based in The Netherlands, offering freelance services in
            planning, digitization, and architecture.
          </p>
        </Section>

        <Section id="services" $scrollProgress={scrollProgress}>
          <h2>Services</h2>
          <ul>
            <li>Digital Transformation (planning, architecture, implementation)</li>
            <li>Full-Stack Development (web, backend, APIs, DBs)</li>
            <li>Cloud Infrastructure (Azure / AWS, DevOps pipelines)</li>
            <li>Generative AI Prototyping (OpenAI, smart workflows)</li>
          </ul>
        </Section>

        <Section id="experience" $scrollProgress={scrollProgress}>
          <h2>Experience</h2>
          <ul>
            <li>City of Amsterdam – Innovation Team: Digital prototypes & public services</li>
            <li>Vodafone IS: Scalable telecom backends</li>
            <li>Bunq: Performance-focused internal systems</li>
          </ul>
        </Section>

        <Section id="technologies" $scrollProgress={scrollProgress}>
          <h2>Technologies</h2>
          <p>React, Node.js, Python, PostgreSQL, Azure, AWS, Kubernetes, Generative AI Integrations</p>
        </Section>

        <Section id="contact" $scrollProgress={scrollProgress}>
          <h2>Contact</h2>
          <p>Let's work together</p>
          <p><a href="mailto:hi@magu.cc">hi@magu.cc</a></p>
          <SocialLinks>
            <SocialLink href="https://www.linkedin.com/in/m%C3%A1ni-gudvardarson-78095929/" target="_blank" rel="noopener noreferrer">
              <FaLinkedin />
              LinkedIn
            </SocialLink>
            <SocialLink href="https://github.com/mani1986" target="_blank" rel="noopener noreferrer">
              <FaGithub />
              GitHub
            </SocialLink>
          </SocialLinks>
        </Section>
      </Container>
    </main>
  );
};

export default App;